import { useEffect, useState } from 'react';
import { Timestamp, doc, runTransaction } from 'firebase/firestore';
import { capacityUpdate } from '../../../utils/workshopCapacity';
import { db } from '../../../firebase';
import {
  WorkshopV2,
  BookingMode,
  ClosingRule,
  WorkshopType,
} from '../../../interfaces/workshop.interface';
import {
  CLOSING_RULE_LABELS,
  computeBookingClosesAt,
  formatClosingDate,
} from '../../../utils/workshopClosing';
import { uploadImage } from '../../../services/uploadImage.service';
import DOMPurify from 'dompurify';

// TipTap
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
// ❌ import { sanitizeHtml } from '../../../utils/sanitizeHtml'; // supprimé

interface Props {
  workshop: WorkshopV2;
  onClose: () => void;
  onUpdated: () => void;
}

const toLocalInputDateTime = (date: Date) => {
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
};

// Sanitize qui **retourne toujours une string**
const sanitizeToString = (html: string): string => {
  return DOMPurify.sanitize(html, {
    RETURN_TRUSTED_TYPE: false,
    ALLOWED_TAGS: ['p', 'ul', 'li', 'strong', 'b', 'u', 'br', 'span'],
    ALLOWED_ATTR: [],
  }) as string; // on force TS à comprendre que c'est une string
};

const EditWorkshopPopup = ({ workshop, onClose, onUpdated }: Props) => {
  const [title, setTitle] = useState(workshop.title);
  const [description, setDescription] = useState(workshop.description || ''); // HTML (string)
  const [date, setDate] = useState(
    toLocalInputDateTime(workshop.date.toDate())
  );
  const [duration, setDuration] = useState(workshop.duration);
  const [type, setType] = useState<WorkshopType>(workshop.type);
  const [bookingMode, setBookingMode] = useState<BookingMode>(workshop.bookingMode);
  const [priceSolo, setPriceSolo] = useState(workshop.priceSolo ?? 0);
  const [priceDuo, setPriceDuo] = useState(workshop.priceDuo ?? 0);
  const [maxParticipants, setMaxParticipants] = useState(
    workshop.maxParticipants
  );
  const [active, setActive] = useState(workshop.active);
  const [closingRule, setClosingRule] = useState<ClosingRule>(
    workshop.closingRule ?? 'NONE'
  );
  // Ne prérempli que pour CUSTOM : sur DAY_BEFORE_18H la date est dérivée, la
  // réafficher comme saisie laisserait croire qu'elle est modifiable telle quelle.
  const [closingDate, setClosingDate] = useState(
    workshop.closingRule === 'CUSTOM' && workshop.bookingClosesAt
      ? toLocalInputDateTime(workshop.bookingClosesAt.toDate())
      : ''
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Popup éditeur
  const [openDescEditor, setOpenDescEditor] = useState(false);

  // TipTap config : gras / souligné / liste à puces (pas de Hx, pas de liens, pas d'orderedList)
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        orderedList: false,
        italic: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        strike: false,
        horizontalRule: false,
      }),
      Underline,
    ],
    content: description || '<p></p>',
    editorProps: {
      attributes: { class: 'max-w-none focus:outline-none min-h-[420px] p-3' },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Tab') {
          event.preventDefault();
          if (event.shiftKey)
            editor?.chain().focus().liftListItem('listItem').run();
          else editor?.chain().focus().sinkListItem('listItem').run();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (openDescEditor && editor) {
      editor.commands.setContent(description || '<p></p>', {
        emitUpdate: false,
      });
    }
  }, [openDescEditor, editor, description]);

  const saveEditor = () => {
    const html = editor?.getHTML() ?? '';
    const clean = sanitizeToString(html); // ← toujours une string
    setDescription(clean);
    setOpenDescEditor(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage(null);
    if (!Number.isInteger(maxParticipants) || maxParticipants < 0) {
      setErrorMessage('Le nombre de places doit être un entier positif ou nul.');
      return;
    }
    setLoading(true);

    try {
      let imageUrl = workshop.imageUrl;
      if (imageFile) {
        const path = `workshops/images/${Date.now()}-${imageFile.name}`;
        imageUrl = await uploadImage(imageFile, path);
      }

      // 🔒 On garantit une string propre ici aussi (au cas où description aurait été modifiée ailleurs)
      const safeDescription: string = sanitizeToString(description ?? '');

      // Recalculé à chaque sauvegarde : si la date de l'atelier est déplacée,
      // la fermeture « la veille à 18h » doit suivre.
      const workshopDate = new Date(date);
      const closesAt = computeBookingClosesAt(
        closingRule,
        workshopDate,
        closingDate ? new Date(closingDate) : null
      );

      const docRef = doc(db, 'workshops_v2', workshop.id);
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(docRef);
        if (!snapshot.exists()) throw new Error('Cet atelier n’existe plus.');
        const current = snapshot.data() as WorkshopV2;
        // An unrelated edit must not overwrite a capacity changed by another admin.
        const placesUpdate = maxParticipants !== workshop.maxParticipants
          ? capacityUpdate(current, maxParticipants)
          : {};
        transaction.update(docRef, {
          title,
          description: safeDescription,
          date: Timestamp.fromDate(workshopDate),
          duration,
          type,
          bookingMode,
          priceSolo,
          priceDuo,
          ...placesUpdate,
          imageUrl,
          active,
          closingRule,
          bookingClosesAt: closesAt ? Timestamp.fromDate(closesAt) : null,
        });
      });

      onUpdated();
      onClose();
    } catch (error) {
      console.error('❌ Erreur update atelier :', error);
      setErrorMessage(error instanceof Error ? error.message : 'Impossible de modifier cet atelier.');
    } finally {
      setLoading(false);
    }
  };

  const Button = ({ onClick, active, children }: any) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded text-sm border ${
        active ? 'bg-[#623413] text-white' : 'bg-white text-gray-700'
      } hover:opacity-90`}
    >
      {children}
    </button>
  );

  // Aperçu de l'échéance : l'admin voit tout de suite ce que la règle donne
  // pour la date saisie, plutôt que de le découvrir après enregistrement.
  const closesAtPreview = computeBookingClosesAt(
    closingRule,
    date ? new Date(date) : null,
    closingDate ? new Date(closingDate) : null
  );
  const closingPreview = closesAtPreview
    ? formatClosingDate(closesAtPreview)
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-scroll">
      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 rounded-lg shadow-lg w-[600px] grid grid-cols-2 gap-4"
      >
        <h2 className="text-lg font-semibold col-span-2">Modifier l’atelier</h2>

        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border px-3 py-2 rounded col-span-2"
          placeholder="Titre"
          required
        />

        {/* Aperçu description + bouton ouvrir éditeur */}
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-gray-600">
              Description (aperçu)
            </label>
            <button
              type="button"
              onClick={() => setOpenDescEditor(true)}
              className="text-sm bg-[#623413] text-white px-3 py-1.5 rounded hover:opacity-90"
            >
              Ouvrir l’éditeur
            </button>
          </div>
          <div
            className="prose prose-neutral max-w-none border rounded p-3 min-h-[96px] bg-gray-50 max-h-36 overflow-y-auto"
            dangerouslySetInnerHTML={{
              __html: description?.trim()
                ? sanitizeToString(description) // sécurité à l’aperçu
                : '<p class="text-gray-400">Aucune description. Cliquez sur “Ouvrir l’éditeur”.</p>',
            }}
          />
        </div>

        <input
          type="datetime-local"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border px-3 py-2 rounded"
          required
        />

        <input
          type="number"
          value={duration}
          onChange={(e) => setDuration(parseInt(e.target.value))}
          className="border px-3 py-2 rounded"
          placeholder="Durée (min)"
        />

        <select
          value={type}
          onChange={(e) => setType(e.target.value as any)}
          className="border px-3 py-2 rounded"
        >
          <option value="public">Tout public</option>
          <option value="health">Santé</option>
          <option value="teambuilding">Teambuilding</option>
        </select>

        <select
          value={bookingMode}
          onChange={(e) => setBookingMode(e.target.value as BookingMode)}
          className="border px-3 py-2 rounded"
        >
          <option value={BookingMode.SOLO}>Individuel uniquement</option>
          <option value={BookingMode.DUO}>Duo uniquement</option>
          <option value={BookingMode.SOLO_DUO}>Individuel ou Duo</option>
        </select>

        {(bookingMode === BookingMode.SOLO || bookingMode === BookingMode.SOLO_DUO) && (
          <input
            type="number"
            value={priceSolo}
            onChange={(e) => setPriceSolo(parseFloat(e.target.value) || 0)}
            className="border px-3 py-2 rounded"
            placeholder="Prix Solo (€)"
          />
        )}

        {(bookingMode === BookingMode.DUO || bookingMode === BookingMode.SOLO_DUO) && (
          <input
            type="number"
            value={priceDuo}
            onChange={(e) => setPriceDuo(parseFloat(e.target.value) || 0)}
            className="border px-3 py-2 rounded"
            placeholder="Prix Duo (€)"
          />
        )}

        <input
          type="number"
          value={maxParticipants}
          onChange={(e) => setMaxParticipants(e.target.valueAsNumber)}
          min={0}
          step={1}
          required
          aria-label="Nombre total de places"
          className="border px-3 py-2 rounded"
          placeholder="Participants max"
        />

        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files?.[0] || null)}
          className="border px-3 py-2 rounded col-span-2"
        />

        <label className="col-span-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Atelier actif
        </label>

        <div className="col-span-2 border-t pt-4">
          <label className="text-sm text-gray-600">
            Fermeture des réservations
          </label>
          <div className="flex gap-4 mt-1">
            <select
              value={closingRule}
              onChange={(e) => setClosingRule(e.target.value as ClosingRule)}
              className="border px-3 py-2 rounded flex-1"
            >
              {(Object.keys(CLOSING_RULE_LABELS) as ClosingRule[]).map(
                (rule) => (
                  <option key={rule} value={rule}>
                    {CLOSING_RULE_LABELS[rule]}
                  </option>
                )
              )}
            </select>

            {closingRule === 'CUSTOM' && (
              <input
                type="datetime-local"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
                className="border px-3 py-2 rounded flex-1"
                required
              />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {closingPreview
              ? `Les réservations fermeront le ${closingPreview}. L'atelier restera visible en grisé jusqu'à son début.`
              : "L'atelier reste réservable jusqu'à son début."}
          </p>
        </div>

        {errorMessage && <p role="alert" className="col-span-2 text-sm text-red-600">{errorMessage}</p>}
        <div className="col-span-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="bg-[#623413] text-white px-4 py-2 rounded"
            disabled={loading}
          >
            {loading ? 'Modification...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* MODAL EDITEUR DESCRIPTION */}
      {openDescEditor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-white w-[1000px] max-w-[95vw] max-h-[85vh] rounded-lg shadow-xl flex flex-col">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold">Éditeur de description</h3>
              <button
                onClick={() => setOpenDescEditor(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Fermer
              </button>
            </div>

            {/* Toolbar */}
            <div className="px-5 py-2 border-b flex flex-wrap gap-2">
              <Button
                onClick={() => editor?.chain().focus().toggleBold().run()}
                active={editor?.isActive('bold')}
              >
                Gras
              </Button>
              <Button
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                active={editor?.isActive('underline')}
              >
                Souligné
              </Button>
              <Button
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                active={editor?.isActive('bulletList')}
              >
                • Liste
              </Button>
              <Button
                onClick={() =>
                  editor?.chain().focus().liftListItem('listItem').run()
                }
              >
                ← Indent-
              </Button>
              <Button
                onClick={() =>
                  editor?.chain().focus().sinkListItem('listItem').run()
                }
              >
                → Indent+
              </Button>
              <Button
                onClick={() =>
                  editor?.chain().focus().unsetAllMarks().clearNodes().run()
                }
              >
                Nettoyer
              </Button>
            </div>

            <div className="p-5 overflow-auto">
              <div className="border rounded">
                <EditorContent editor={editor} />
              </div>
              <style>
                {`
                  .ProseMirror ul { list-style: disc; padding-left: 1.25rem; }
                  .ProseMirror li { margin: 0.25rem 0; }
                `}
              </style>
            </div>

            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => setOpenDescEditor(false)}
                className="px-4 py-2 text-sm rounded border"
              >
                Annuler
              </button>
              <button
                onClick={saveEditor}
                className="px-4 py-2 text-sm rounded bg-[#623413] text-white"
              >
                Enregistrer la description
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditWorkshopPopup;
